import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import { ProjectCard } from "@govlaunch/project";
import { LoadingSpinner } from "@govlaunch/web";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import useIsMobile from "~/lib/hooks/useIsMobile";
import { IGovernmentQuery } from "~/lib/queries/__generated__/GovernmentQuery.generated";
import { IProjectsByGovernmentQuery } from "~/lib/queries/__generated__/ProjectsByGovernmentQuery.generated";
import { useIntlLocale } from "~/lib/withIntl";
import TranslationButton from "~/src/components/translation/TranslationButton";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import mediaQueries from "~/components/mediaQueries";
import EmptyProjectSlot from "~/components/project/EmptyProjectSlot";

interface IOwnGovernmentProjectsProps {
  loading: boolean;
  government: NonNullable<IGovernmentQuery["government"]>;
  projectsByGovernment: NonNullable<IProjectsByGovernmentQuery["projectsByGovernment"]>;
  onShowMore: () => any;
  onAddProject: () => void;
}

export default function OwnGovernmentProjects({
  loading = false,
  government,
  projectsByGovernment,
  onShowMore,
  onAddProject,
}: IOwnGovernmentProjectsProps) {
  const intl = useIntl();
  const router = useRouter();
  const isMobile = useIsMobile();
  const { locale } = useIntlLocale();

  if (!projectsByGovernment.items) {
    return null;
  }

  return (
    <div
      css={{
        display: "grid",
        gridRowGap: 28,
      }}
    >
      <div
        data-intercom-target="Government Projects"
        css={mediaQueries({
          display: "grid",
          gridTemplateColumns: ["1fr", "1fr 1fr"],
          gridColumnGap: 16,
          gridRowGap: 16,
        })}
      >
        {government.viewerIsMember && (
          <EmptyProjectSlot
            onClick={onAddProject}
            text={intl.formatMessage({ defaultMessage: "Add another project", id: "B0aG5V" })}
          />
        )}

        {projectsByGovernment.items.map((project) => {
          const linkHref = isMobile
            ? `/projects/${project.slug}`
            : appendNextLinkHrefParams(router, {
                projectSlug: project.slug,
              });

          const translationLinkHref = isMobile
            ? `/projects/${project.slug}`
            : appendNextLinkHrefParams(router, {
                projectSlug: project.slug,
                lang: locale,
              });

          const translationLinkWrap = () => {
            return (
              <TranslationButton
                paddingY="4px"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  router.push(translationLinkHref);
                }}
              />
            );
          };

          return (
            <Link
              href={linkHref}
              as={isMobile ? `/projects/${project.slug}` : `/governments/${government.slug}/projects/${project.slug}`}
              passHref={true}
              key={project._id}
              scroll={false}
            >
              <ProjectCard
                css={!isMobile ? { maxWidth: "385px" } : {}}
                project={project}
                translationLinkWrap={locale !== "en-US" ? translationLinkWrap : undefined}
              />
            </Link>
          );
        })}
        {loading && <LoadingSpinner />}
      </div>

      {projectsByGovernment.pageInfo &&
        projectsByGovernment.pageInfo.hasNextPage &&
        projectsByGovernment.pageInfo.endCursor && (
          <Button
            onClick={() => onShowMore()}
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              margin: 1,
              padding: 10,
              color: palette.primary,
              fontWeight: 500,
              fontSize: 14,
              lineHeight: 1.33,
              letterSpacing: 1,
              textTransform: "uppercase",
              backgroundColor: "transparent",
              border: "none",
              outline: "none",
              cursor: "pointer",
              "&:active": {
                opacity: 0.7,
              },
              marginmt: 0,
            }}
            disabled={false}
          >
            <FormattedMessage id="aWpBzj" defaultMessage="Show more" />
          </Button>
        )}
    </div>
  );
}
